/** @prettier */

import {Tabs, Tab, Box, Container, Paper, Grid, Stack} from '@mui/material';
import {LoaderFunctionArgs, useLoaderData, useParams} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

//MTH component import
import SingleGroupSelectBox from './GroupSelectBox';
import {ScheduleItem} from './ScheduleItem';
import APIErrorMessage from '../../component/APIErrorMessage';
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import CopyOtherGroupDialog from './CopyOtherGroupDialog';
import ScheduleSettingDialog from './ScheduleSettingDialog';
import CopyToTemplateDialog from './CopyToTemplateDialog';
import UploadFileDialog from './UploadFileDialog';
import DownloadFileDialog from './DownloadFileDialog';
import {AddButton} from '../../component/BasicButton';
//API import
import {
  getCompositionsGroups,
  getCompositionsSitesBySiteID,
  getDailySchedule,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';

import {limit} from '../../define';
import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';

type LoaderReturnType = {
  /** サイト名称 */
  siteName: apiIF.res.getCompositionsSitesBySiteID['site-name'];
  /** グループ情報 */
  groupInfo: apiIF.res.getCompositionsGroups['group-informations'];
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //サイトの情報を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //グループの情報の取得
    promises.push(
      getCompositionsGroups(params.siteID, {
        'fields': 'group-name,unit-system',
        'max-result': limit.REGISTRATION_NUM_GROUP_MAX,
      })
    );
    //ユニットの登録状況を取得
    promises.push(checkUnitExistence(params.siteID, true));
    const response = await Promise.all(promises);

    const siteInfo = response[0] as apiIF.res.getCompositionsSitesBySiteID;
    const groupInfo = response[1] as apiIF.res.getCompositionsGroups;
    const unitExistence = response[2] as unitExistence;

    if (
      unitExistence.hasAirconIU === false ||
      (siteInfo['site-authority'] !== 'admin' &&
        siteInfo['site-authority'] !== 'manager')
    ) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    return {
      siteName: siteInfo['site-name'],
      groupInfo: groupInfo['group-informations'].filter(group => {
        //本来はバックエンド側でやるべき
        //2ndStepではリリース優先でフロントエンド側で行った
        return group['unit-system'] === 'SL';
      }),
    };
  } catch {
    return undefined;
  }
}

/** 特定日スケジュール取得データの初期値 */
export const dailyScheduleDataInitial = {
  'weekday-schedule': [],
  'holiday-schedule': [],
  'special-day-1-schedule': [],
  'special-day-2-schedule': [],
};

/** タブパネルの型 */
type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};
/**
 * タブパネルの表示
 * @param children タブパネル内に表示するNode
 * @param value 現在設定中のタブ
 * @param index タブ毎に割り付けられた数字
 * @returns JSX
 */
const TabPanel = (props: TabPanelProps) => {
  const {children, value, index} = props;
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`daily-tabpanel-${index}`}
      aria-labelledby={`daily-tab-${index}`}>
      {value === index && (
        <Box className="schedule__tabPanel">
          <Box>{children}</Box>
        </Box>
      )}
    </Box>
  );
};

/**
 * 特定日スケジュール
 */
const DailySchedule = () => {
  const trans = useTranslation().t; //翻訳関数
  //loaderのデータを取得
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  //パスパラメータを取得
  const {siteID} = useParams();

  /** サイト名称 */
  const [siteName, setSiteName] = useState<LoaderReturnType['siteName']>('');
  //prettier-ignore
  /** グループ情報 */
  const [groupInfo, setGroupInfo] = useState<LoaderReturnType['groupInfo'] | null>(null);
  /** セレクトボックスで選択したグループのID */
  const [groupID, setGroupID] = useState('');
  /** セレクトボックスで選択したグループの名前 */
  const [groupName, setGroupName] = useState<string>('');
  /** スケジュール追加ダイアログ表示フラグ */
  const [isOpenAddDialog, setIsOpenAddDialog] = useState(false);
  /** 特定日スケジュールの更新フラグ */
  const [hasScheduleChanged, setHasScheduleChanged] = useState(false);
  /** 選択したグループの特定日のスケジュールを他のグループにコピーするダイアログ表示フラグ */
  const [isCopyOtherGroupDialog, setIsCopyOtherGroupDialog] = useState(false);
  /** 選択したグループの指定した特定日のスケジュールを同じグループの別の特定日にコピーするダイアログ表示フラグ*/
  const [isCopyToTemplateDialog, setIsCopyToTemplateDialog] = useState(false);
  /** 選択したグループの指定した特定日のスケジュールをアップロードするダイアログ表示フラグ */
  const [isUploadFileDialog, setIsUploadFileDialog] = useState(false);
  /** 選択したグループの指定した特定日にスケジュールをダウンロードするダイアログ表示フラグ */
  const [isDownloadFileDialog, setIsDownloadFileDialog] = useState(false);
  /** 選択状態のスケジュール行 */
  const [selectedRow, setSelectedRow] = useState<number | string>('');
  /** 表示する特定日 */
  const [type, setType] =
    useState<keyof apiIF.res.getDailySchedule>('weekday-schedule');
  /** 現在選択中のタブを示す文字列 */
  const [tabName, setTabName] = useState(0);
  /** 特定日スケジュールのレスポンスデータ*/
  const [dailyScheduleData, setDailyScheduleData] =
    useState<apiIF.res.getDailySchedule>(dailyScheduleDataInitial);
  /** 特定日スケジュールの設定(APIに渡す用) */
  const [dailyScheduleRequestData, setDailyScheduleRequestData] =
    useState<apiIF.reqParameters.patchDailySchedule>({});
  /** APIエラー時エラーメッセージ表示 */
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);

  /**
   * タブに付与するプロパティ
   * @param index タブ毎に割り付けられた数字
   * @returns プロパティ
   */
  const a11yProps = (index: number) => {
    return {
      'id': `daily-tab-${index}`,
      'aria-controls': `daily-tabpanel-${index}`,
    };
  };

  useEffect(() => {
    if (loaderData !== undefined) {
      setSiteName(loaderData.siteName);
      setGroupInfo(loaderData.groupInfo);
      if (loaderData.groupInfo.length > 0) {
        setGroupID(loaderData.groupInfo[0]['group-id']);
      }
    }
  }, [loaderData]);

  //特定日スケジュール設定の取得
  useEffect(() => {
    if (siteID === undefined) return;
    if (groupID === '') return;
    if (groupInfo === null) return;

    /** 選択したグループの情報 */
    const targetGroup = groupInfo.find(item => {
      return item['group-id'] === groupID;
    });
    if (targetGroup !== undefined && targetGroup['group-name'] !== undefined) {
      setGroupName(targetGroup['group-name']);
    }
    getDailySchedule(siteID, groupID).then(response => {
      //レスポンスデータを格納
      setDailyScheduleData(response);
      //レスポンスデータをBody用の変数に変形
      setDailyScheduleRequestData(responseToBodyData(response));
    });
  }, [siteID, groupID, groupInfo, hasScheduleChanged]);

  /** スケジュール更新関数 */
  const scheduleChangeHandler = () => {
    setHasScheduleChanged(!hasScheduleChanged);
  };

  /**
   * 特定日スケジュールのレスポンスデータを、Bodyデータに変形する
   * @param response 特定日スケジュールのレスポンスデータ
   * @returns Bodyデータ
   */
  const responseToBodyData = (response: apiIF.res.getDailySchedule) => {
    //すべて未設定のデータを初期値として格納
    let bodyData: apiIF.reqParameters.patchDailySchedule = {};
    //prettier-ignore
    //4つの特定日をループ
    (Object.keys(response) as (keyof apiIF.res.getDailySchedule)[]).forEach((key)=>{
      bodyData[key] = {schedule : response[key]}
    })
    return bodyData;
  };

  /**
   * タブ切り替え時の処理
   * @param event
   * @param newValue 選択したタブナンバー
   */
  const tabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabName(newValue);
    switch (newValue) {
      case 0:
        setType('weekday-schedule');
        setSelectedRow('');
        break;
      case 1:
        setType('holiday-schedule');
        setSelectedRow('');
        break;
      case 2:
        setType('special-day-1-schedule');
        setSelectedRow('');
        break;
      case 3:
        setType('special-day-2-schedule');
        setSelectedRow('');
        break;
    }
  };

  /**
   * ADD SCHEDULEボタン押下時処理
   */
  const handleAddSchedule = () => {
    setIsOpenAddDialog(true);
  };

  /**
   * Copy other group押下時処理
   */
  const copyOtherGroupDialog = () => {
    setIsCopyOtherGroupDialog(true);
  };

  /**
   * Copy to template押下時処理
   */
  const copyToTemplateDialog = () => {
    setIsCopyToTemplateDialog(true);
  };

  /**
   * Upload file押下時処理
   */
  const uploadFileDialog = () => {
    setIsUploadFileDialog(true);
  };

  /**
   * Upload file押下時処理
   */
  const downloadFileDialog = () => {
    setIsDownloadFileDialog(true);
  };

  /**
   * 歯車メニューリスト
   * @param groupID グループID
   * @returns メニュー一覧
   */
  const gearMenuItem = (groupID: string): GearMenuListArgType['menuItem'] => {
    return [
      {
        id: `mv-copyOtherGroup${groupID}`,
        label: trans('ScheduleTemplate.gearMenuLabel.copyOtherGroup'),
        onClick: () => copyOtherGroupDialog(),
      },
      {
        id: `mv-copyToTemplate${groupID}`,
        label: trans('ScheduleTemplate.gearMenuLabel.copyToTemplate'),
        onClick: () => copyToTemplateDialog(),
      },
      {
        id: `mv-uploadFile${groupID}`,
        label: trans('ScheduleTemplate.gearMenuLabel.uploadFile'),
        onClick: () => uploadFileDialog(),
      },
      {
        id: `mv-downloadFile${groupID}`,
        label: trans('ScheduleTemplate.gearMenuLabel.downloadFile'),
        onClick: () => downloadFileDialog(),
      },
    ];
  };

  if (siteID === undefined) return <></>;
  return (
    <Container fixed>
      <Stack>
        {/** タイトル */}
        <Box>
          <h1>{trans('ScheduleTemplate.title')}</h1>
        </Box>
        {/** スケジュール */}
        <Box className="schedule__contents">
          <Grid container rowSpacing={3}>
            {/** サイトの名前 */}
            <Grid item xs={12} className="schedule__contents__siteName">
              {siteName}
            </Grid>
            {/** グループ選択欄 */}
            <Grid
              item
              xs={12}
              container
              alignItems="flex-end"
              columnSpacing={3}
              className="schedule__contents__groupSelectArea">
              {/** グループ選択 */}
              <Grid item xs="auto">
                {trans('ScheduleTemplate.menu.group')}
              </Grid>
              <Grid item xs>
                {groupID !== '' && groupInfo !== null ? (
                  <SingleGroupSelectBox
                    groupID={groupID}
                    setGroupID={setGroupID}
                    groupInfo={groupInfo}
                  />
                ) : (
                  <></>
                )}
              </Grid>
              {/** 歯車アイコン */}
              {/* グループIDが未選択の場合、歯車アイコンを表示しない */}
              <Grid item xs="auto">
                {groupID !== '' && groupName !== '' ? (
                  <GearMenu menuItem={gearMenuItem(groupID)} />
                ) : (
                  <></>
                )}
              </Grid>
            </Grid>
            {/** 特定日の種類選択欄、スケジュール追加ボタン */}
            <Grid item xs={12} container>
              {/** 特定日の種類選択欄 */}
              <Grid
                item
                xs="auto"
                className="schedule__contents__specificScheduleSelectArea">
                <Tabs
                  value={tabName}
                  onChange={tabChange}
                  aria-label="daily-tabs">
                  <Tab
                    label={trans('ScheduleTemplate.tabLabel.weekday')}
                    {...a11yProps(0)}
                  />
                  <Tab
                    label={trans('ScheduleTemplate.tabLabel.holiday')}
                    {...a11yProps(1)}
                  />
                  <Tab
                    label={trans('ScheduleTemplate.tabLabel.specialDay1')}
                    {...a11yProps(2)}
                  />
                  <Tab
                    label={trans('ScheduleTemplate.tabLabel.specialDay2')}
                    {...a11yProps(3)}
                  />
                </Tabs>
              </Grid>
              {/** スケジュール追加ボタン */}
              <Grid item xs className="schedule__contents__addButton">
                <AddButton
                  onClick={handleAddSchedule}
                  id="mv-btnAddSchedule"
                  disabled={
                    groupID === '' ||
                    /* スケジュールが16個以上の場合は追加ボタンを非アクティブ化 */
                    dailyScheduleData[type].length >=
                      limit.MAX_NUM_SETTING_SCHEDULE
                  }
                  buttonText={trans('ScheduleTemplate.menu.addButton')}
                />
              </Grid>
            </Grid>
            {/** 一覧 */}
            <Grid item xs={8}>
              <TabPanel value={tabName} index={0}>
                <DailyScheduleView
                  siteID={siteID}
                  groupID={groupID}
                  type="weekday-schedule"
                  schedule={dailyScheduleData}
                  dailyScheduleRequestData={dailyScheduleRequestData}
                  scheduleChangeHandler={scheduleChangeHandler}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </TabPanel>
              <TabPanel value={tabName} index={1}>
                <DailyScheduleView
                  siteID={siteID}
                  groupID={groupID}
                  type="holiday-schedule"
                  schedule={dailyScheduleData}
                  dailyScheduleRequestData={dailyScheduleRequestData}
                  scheduleChangeHandler={scheduleChangeHandler}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </TabPanel>
              <TabPanel value={tabName} index={2}>
                <DailyScheduleView
                  siteID={siteID}
                  groupID={groupID}
                  type="special-day-1-schedule"
                  schedule={dailyScheduleData}
                  dailyScheduleRequestData={dailyScheduleRequestData}
                  scheduleChangeHandler={scheduleChangeHandler}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </TabPanel>
              <TabPanel value={tabName} index={3}>
                <DailyScheduleView
                  siteID={siteID}
                  groupID={groupID}
                  type="special-day-2-schedule"
                  schedule={dailyScheduleData}
                  dailyScheduleRequestData={dailyScheduleRequestData}
                  scheduleChangeHandler={scheduleChangeHandler}
                  selectedRow={selectedRow}
                  setSelectedRow={setSelectedRow}
                />
              </TabPanel>
            </Grid>
          </Grid>
        </Box>
      </Stack>
      {groupID !== '' ? (
        <>
          {/** スケジュール追加ダイアログ */}
          <ScheduleSettingDialog
            targetGroupID={groupID}
            schedule={{'command-time': '12:00'}}
            specificDayType={type}
            isOpenDialog={isOpenAddDialog}
            setIsOpenDialog={setIsOpenAddDialog}
            scheduleChangeHandler={scheduleChangeHandler}
            allDailySchedule={dailyScheduleData}
          />
          {/** グループ内の別の特定日へスケジュールをコピーするダイアログ */}
          <CopyToTemplateDialog
            isOpenDialog={isCopyToTemplateDialog}
            setIsOpenDialog={setIsCopyToTemplateDialog}
            scheduleChangeHandler={scheduleChangeHandler}
            dailyScheduleData={dailyScheduleData}
            targetGroupID={groupID}
            targetGroupName={groupName}
          />
          {/** アップロードされたファイルのスケジュールデータを選択した特定日のスケジュールへコピーするダイアログ */}
          <UploadFileDialog
            isOpenDialog={isUploadFileDialog}
            setIsOpenDialog={setIsUploadFileDialog}
            scheduleChangeHandler={scheduleChangeHandler}
            targetGroupID={groupID}
            targetGroupName={groupName}
          />
          {/** 選択した特定日のスケジュールデータをダウンロードするダイアログ */}
          <DownloadFileDialog
            groupID={groupID}
            isOpenDialog={isDownloadFileDialog}
            setIsOpenDialog={setIsDownloadFileDialog}
            targetGroupName={groupName}
          />
        </>
      ) : (
        <></>
      )}
      {/** 他グループへスケジュールをコピーするダイアログ */}
      {groupID !== '' && groupInfo !== null ? (
        <CopyOtherGroupDialog
          isOpenDialog={isCopyOtherGroupDialog}
          setIsOpenDialog={setIsCopyOtherGroupDialog}
          scheduleChangeHandler={scheduleChangeHandler}
          dailyScheduleData={dailyScheduleData}
          groupID={groupID}
          groupName={groupName}
          groupInfo={groupInfo}
        />
      ) : (
        <></>
      )}
      {/* APIエラー時のメッセージ */}
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={'mv-todayScheduleSaveErrDlgBtn'}
      />
    </Container>
  );
};

/**
 * 1つの特定日に対して、スケジュールを表示するコンポーネント
 * @param props.type 特定日
 * @returns JSX
 */
const DailyScheduleView = (props: {
  siteID: string;
  groupID: string;
  type: keyof apiIF.res.getDailySchedule;
  schedule: apiIF.res.getDailySchedule;
  dailyScheduleRequestData: apiIF.reqParameters.patchDailySchedule;
  scheduleChangeHandler: () => void;
  selectedRow: string | number;
  setSelectedRow: React.Dispatch<React.SetStateAction<string | number>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  if (props.groupID === '') return <></>;
  if (props.schedule[props.type].length === 0) {
    return <Box>{trans('ScheduleTemplate.notSet')}</Box>;
  }
  return (
    <Stack component={Paper} className="schedule__table">
      {/** ヘッダ */}
      <Grid container alignItems="center" className="schedule__table__header">
        {/** 時刻 */}
        <Grid item xs={2.5} className="schedule__table__header__item">
          {trans('ScheduleTemplate.list.time')}
        </Grid>
        {/** 指令値 */}
        <Grid item xs={4.5} className="schedule__table__header__item">
          {trans('ScheduleTemplate.list.action')}
        </Grid>
        {/** 操作アイコン */}
        <Grid item xs className="schedule__table__header__item">
          {trans('ScheduleTemplate.list.operation')}
        </Grid>
      </Grid>
      {/** ボディ */}
      {/* 設定済みのスケジュールを表示する */}
      <Stack className="schedule__table__body--specificSchedule">
        {props.schedule[props.type].map((schedule, index) => {
          return (
            <Box key={`${props.type}${index}`}>
              <ScheduleItem
                scheduleItemData={schedule}
                groupID={props.groupID}
                index={index}
                scheduleChangeHandler={props.scheduleChangeHandler}
                selectedRow={props.selectedRow}
                setSelectedRow={props.setSelectedRow}
                type={props.type}
                dailyScheduleRequestData={props.dailyScheduleRequestData}
                dailyScheduleData={props.schedule}
              />
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default DailySchedule;
